// Drag-to-reorder table columns. A table opts in with data-reorder-list-key (and data-column-order
// when the user has a saved order); th/td are matched by data-column-key. Order persists per user.
import Sortable from 'sortablejs';

var TABLE_SELECTOR = 'table[data-reorder-list-key]';
var SAVE_DELAY = 300;
var saveTimers = {};

function csrfToken() {
  var meta = document.querySelector('[name="csrf-token"]');
  return meta ? meta.content : null;
}

function keyedCells(row) {
  return Array.prototype.filter.call(row.children, function (cell) { return cell.dataset.columnKey; });
}

function headerRow(table) {
  return table.querySelector(':scope > thead > tr');
}

function tableRows(table) {
  return table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr');
}

function headerKeys(table) {
  var row = headerRow(table);
  return row ? keyedCells(row).map(function (cell) { return cell.dataset.columnKey; }) : [];
}

// saved keys first; a column the user never ordered goes after its default predecessor
function mergeOrder(defaultKeys, savedKeys) {
  var result = savedKeys.filter(function (key) { return defaultKeys.indexOf(key) !== -1; });
  result = result.filter(function (key, index) { return result.indexOf(key) === index; });

  defaultKeys.forEach(function (key, index) {
    if (result.indexOf(key) !== -1) return;
    var previous = index > 0 ? defaultKeys[index - 1] : null;
    result.splice(previous ? result.indexOf(previous) + 1 : 0, 0, key);
  });
  return result;
}

// keyed cells swap among the positions keyed cells already occupy, so unkeyed cells (row number, actions) stay put
function reorderRow(row, order) {
  var cells = keyedCells(row);
  if (cells.length < 2) return;

  var rank = function (cell) {
    var index = order.indexOf(cell.dataset.columnKey);
    return index === -1 ? order.length : index;
  };
  var sorted = cells.map(function (cell, index) { return { cell: cell, index: index }; })
    .sort(function (a, b) { return rank(a.cell) - rank(b.cell) || a.index - b.index; })
    .map(function (entry) { return entry.cell; });
  if (sorted.every(function (cell, index) { return cell === cells[index]; })) return;

  var markers = cells.map(function (cell) {
    var marker = document.createComment('');
    cell.replaceWith(marker);
    return marker;
  });
  markers.forEach(function (marker, index) { marker.replaceWith(sorted[index]); });
}

function applyOrder(table, savedKeys) {
  var order = mergeOrder(headerKeys(table), savedKeys);
  Array.prototype.forEach.call(tableRows(table), function (row) { reorderRow(row, order); });
  table.classList.add('columns-ordered');
}

function tablesFor(listKey) {
  return Array.prototype.filter.call(document.querySelectorAll(TABLE_SELECTOR), function (table) {
    return table.dataset.reorderListKey === listKey;
  });
}

function selectorFor(listKey) {
  return Array.prototype.find.call(document.querySelectorAll('.column-selector'), function (selector) {
    return selector.dataset.listKey === listKey;
  });
}

function selectorOptions(selector) {
  return Array.prototype.filter.call(selector.querySelectorAll('.column-list .option'), function (option) {
    return option.querySelector('.column-checkbox:not([id$="-select-all"])');
  });
}

function optionKey(option) {
  return option.querySelector('.column-checkbox').value;
}

function applySelectorOrder(selector, savedKeys) {
  var options = selectorOptions(selector);
  if (options.length < 2) return;

  var order = mergeOrder(options.map(optionKey), savedKeys);
  var anchor = options[options.length - 1].nextSibling;
  var parent = options[0].parentNode;
  order.forEach(function (key) {
    var option = options.find(function (candidate) { return optionKey(candidate) === key; });
    parent.insertBefore(option, anchor);
  });
}

function applyEverywhere(listKey, savedKeys) {
  tablesFor(listKey).forEach(function (table) {
    applyOrder(table, savedKeys);
    // keep the attribute current so a Turbolinks cache restore doesn't re-apply a stale order
    table.dataset.columnOrder = JSON.stringify(savedKeys);
  });
  var selector = selectorFor(listKey);
  if (selector) applySelectorOrder(selector, savedKeys);
}

function persistOrder(listKey, keys) {
  clearTimeout(saveTimers[listKey]);
  saveTimers[listKey] = setTimeout(function () {
    fetch('/column_preferences/' + encodeURIComponent(listKey), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      credentials: 'same-origin',
      body: JSON.stringify({ column_order: keys })
    }).catch(function (error) {
      if (window.rollbar) window.rollbar.error('Error saving column order:', error);
    });
  }, SAVE_DELAY);
}

function setDraggingKey(table, key, dragging) {
  table.querySelectorAll('[data-column-key="' + key + '"]').forEach(function (cell) {
    cell.classList.toggle('column-dragging', dragging);
  });
}

function initHeaderSortable(table) {
  var row = headerRow(table);
  if (!row || Sortable.get(row)) return;

  keyedCells(row).forEach(function (th) { th.title = th.title || 'Drag to reorder column'; });

  Sortable.create(row, {
    draggable: 'th[data-column-key]:not(.d-none)',
    filter: '.column-header-menu',
    preventOnFilter: false,
    animation: 150,
    ghostClass: 'column-drag-ghost',
    chosenClass: 'column-drag-chosen',
    onStart: function (event) { setDraggingKey(table, event.item.dataset.columnKey, true); },
    onEnd: function (event) {
      setDraggingKey(table, event.item.dataset.columnKey, false);
      if (event.oldIndex === event.newIndex) return;

      var listKey = table.dataset.reorderListKey;
      var keys = headerKeys(table);
      applyEverywhere(listKey, keys);
      persistOrder(listKey, keys);
    }
  });
}

function initSelectorSortable(selector) {
  var list = selector.querySelector('.column-list');
  if (!list || Sortable.get(list)) return;

  Sortable.create(list, {
    draggable: '.option:not(.select-all-option)',
    handle: '.column-drag-handle',
    animation: 150,
    ghostClass: 'column-drag-ghost',
    onEnd: function (event) {
      if (event.oldIndex === event.newIndex) return;

      var listKey = selector.dataset.listKey;
      var keys = selectorOptions(selector).map(optionKey);
      tablesFor(listKey).forEach(function (table) {
        applyOrder(table, keys);
        table.dataset.columnOrder = JSON.stringify(keys);
      });
      persistOrder(listKey, keys);
    }
  });
}

function initTable(table) {
  var saved = [];
  try { saved = JSON.parse(table.dataset.columnOrder || '[]'); } catch (e) { saved = []; }

  applyOrder(table, saved);
  initHeaderSortable(table);

  var selector = selectorFor(table.dataset.reorderListKey);
  if (selector) {
    applySelectorOrder(selector, saved);
    initSelectorSortable(selector);
  }
}

function initAll() {
  document.querySelectorAll(TABLE_SELECTOR).forEach(initTable);
}

document.addEventListener('turbolinks:load', initAll);

// filter/sort/pagination re-render the results via AJAX, so new tables show up without a page load
var observer = new MutationObserver(function (mutations) {
  var found = [];
  mutations.forEach(function (mutation) {
    mutation.addedNodes.forEach(function (node) {
      if (node.nodeType !== 1) return;
      if (node.matches(TABLE_SELECTOR)) found.push(node);
      else node.querySelectorAll && node.querySelectorAll(TABLE_SELECTOR).forEach(function (table) { found.push(table); });
    });
  });
  if (!found.length) return;

  found.forEach(initTable);
  observer.takeRecords(); // drop the mutations our own reordering just caused
});
observer.observe(document.documentElement, { childList: true, subtree: true });

$(document).on('click', '.column-selector .reset-columns', function () {
  var selector = $(this).closest('.column-selector')[0];
  var defaultOrder = JSON.parse(selector.dataset.defaultOrder || '[]');
  clearTimeout(saveTimers[selector.dataset.listKey]); // a pending save would undo the reset
  applyEverywhere(selector.dataset.listKey, defaultOrder);
  tablesFor(selector.dataset.listKey).forEach(function (table) { table.removeAttribute('data-column-order'); });
});
